import * as React from "react";

import { splitNativeProps, type NativeMetadataProps } from "./apple-native";
import { cn } from "./utils";

const filterGeneratedProps = (props: any) => {
  const clean: any = {};
  for (const key in props) {
    if (!key.startsWith("_fg")) {
      clean[key] = props[key];
    }
  }
  return clean;
};

export type TextareaProps = React.ComponentProps<"textarea"> & NativeMetadataProps & {
  /** Use for narrative fields that may need more room. Enables vertical resize
   *  and a larger initial editing area without allowing the field to widen. */
  longDescription?: boolean;
  resizeMode?: "none" | "vertical" | "both";
};

const Textarea = React.forwardRef<
  HTMLTextAreaElement,
  TextareaProps
>(
  ({ className, longDescription = false, resizeMode, ...props }, ref) => {
    const generatedId = React.useId();
    const cleanProps = filterGeneratedProps(props);
    const { nativeProps, rest } = splitNativeProps(cleanProps);
    const effectiveResizeMode = resizeMode ?? (longDescription ? "vertical" : "none");
    return (
      <textarea
        ref={ref}
        id={rest.id ?? rest.name ?? generatedId}
        data-slot="textarea"
        data-native-role={nativeProps["data-native-role"] ?? "text-area"}
        data-long-description={longDescription || undefined}
        className={cn(
          "border-input placeholder:text-[rgba(60,60,67,0.6)] dark:placeholder:text-[rgba(235,235,245,0.6)] focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 flex min-h-16 w-full rounded-[var(--ui-control-radius)] border bg-input-background px-3 py-2 text-base transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50",
          longDescription && "min-h-24",
          effectiveResizeMode === "vertical" && "resize-y [field-sizing:fixed]",
          effectiveResizeMode === "both" && "resize [field-sizing:fixed]",
          effectiveResizeMode === "none" && "resize-none field-sizing-content",
          className,
        )}
        {...nativeProps}
        {...rest}
      />
    );
  }
);

Textarea.displayName = "Textarea";

export { Textarea };
